import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  type Dispatch,
  type PropsWithChildren,
} from "react";
import { installExternalStoreBridge, syncLegacyStateFromReact } from "../state/store.js";
import { initialState } from "./initialState.ts";
import { appReducer } from "./reducer.ts";
import type { AppAction, AppState } from "../types.ts";

type AppContextValue = {
  state: AppState;
  dispatch: Dispatch<AppAction>;
  patch: (patch: Partial<AppState>) => void;
  getState: () => AppState;
};

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: PropsWithChildren) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  const patch = useCallback((partial: Partial<AppState>) => {
    dispatch({ type: "PATCH", patch: partial });
  }, []);

  const getState = useCallback(() => stateRef.current, []);

  useLayoutEffect(() => {
    installExternalStoreBridge({ patch });
    return () => installExternalStoreBridge();
  }, [patch]);

  useLayoutEffect(() => {
    syncLegacyStateFromReact(state);
  }, [state]);

  const value = useMemo(() => ({ state, dispatch, patch, getState }), [state, patch, getState]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppState() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppState must be used inside AppProvider");
  return ctx.state;
}

export function useAppDispatch() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppDispatch must be used inside AppProvider");
  return ctx.dispatch;
}

export function useAppActions() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppActions must be used inside AppProvider");
  return ctx;
}
